import {
  Component,
  Input,
  OnInit,
  OnChanges,
  SimpleChanges,
  ChangeDetectorRef,
  OnDestroy,
  EventEmitter,
  Output,
} from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, UntypedFormControl } from '@angular/forms';
import { CarrierListResponse, fieldsConfig, ShipmentExtras, Options, ShopSettings } from '../../../types';
import { debounceTime, distinctUntilChanged, finalize, startWith, switchMap, tap } from 'rxjs/operators';
import { FormService } from '../../../services';
import { ShopService } from '@app/services/shops/shop.service';
import { FieldsTypes, internationalMethods, ShippingMethod } from '@pages/checkout-app/types/types';
import { CarrierProvidersNames, carrierProviderDisplayNameMap } from '@pdqdev/pdq-shared/dist/CarrierData';
import { Subscription, Subject } from 'rxjs';
import { CheckoutService } from '@pages/checkout-app/checkout.service';
import { Carrier } from '@app/services/carriers/carrier.model';
import { CarrierProviders } from '@pdqdev/pdq-shared/dist/entities';
import { DropdownOption } from '@storybook/components/dropdown/dropdown.component';
import { isCarrierShippable, displayProvidersData } from '@pages/checkout-app/utils/carrier.utils';

@Component({
  selector: 'app-recommended-delivery',
  templateUrl: './recommended-delivery.component.html',
  styleUrls: ['./recommended-delivery.component.scss'],
  standalone: false,
})
export class RecommendedDeliveryComponent implements OnInit, OnChanges, OnDestroy {
  @Input() formGroup: UntypedFormGroup;
  @Input() shipmentExtrasFormGroup: UntypedFormGroup;
  @Input() deliveryType: string;
  @Input() shippingMethodCategory: string;
  @Input() showFormErrors: boolean;
  @Input() loadedCarrier: Carrier;
  @Input() shipmentExtrasValues: ShipmentExtras;
  @Input() shopSettings: ShopSettings;
  @Input() polygonAddress;
  @Output() resetShopSettings = new EventEmitter();
  // the saved extras finished loading; edit pages take them as part of the saved values
  @Output() shipmentExtrasLoaded = new EventEmitter<void>();
  searchCarrier: string | any = '';
  originalShopSettings: ShopSettings;

  isCarrierAdvancedSettings: boolean = false;
  isCarrierAutocompleteLoading: boolean = false;
  isActiveCarrierHaveShipmentExtrasFields: boolean = false;
  shipmentExtrasFields: fieldsConfig[] = [];
  fieldsTypes = FieldsTypes;

  mapOption: DropdownOption;

  isActiveServiceProviders: boolean = false;
  selectedActiveProviders: CarrierProvidersNames[] | string[] = [];
  displayProvidersInfoOrServiceLevels: 'providers' | 'providerServices' = 'providers';
  providerServices: string[];

  carrierAutocompleteResults: Carrier[] = [];
  activeCarrier: Carrier = null;
  carrierMetadata;

  formResetSubscription$: Subscription;

  private destroy$ = new Subject();

  constructor(
    private formBuilder: UntypedFormBuilder,
    private formService: FormService,
    private shopService: ShopService,
    private changeDetectorRef: ChangeDetectorRef,
    private checkoutService: CheckoutService,
  ) {}

  ngOnInit() {
    this.formResetSubscription$ = this.checkoutService.onFormReset$.subscribe((toReset) => {
      if (toReset) {
        this.resetForm();
      }
    });
  }

  ngOnDestroy() {
    this.formResetSubscription$?.unsubscribe();
    this.destroy$.next(true);
    this.destroy$.complete();
  }

  private resetForm(): void {
    this.shipmentExtrasFormGroup.reset();
    this.isActiveServiceProviders = false;
    this.isCarrierAdvancedSettings = false;
    this.isActiveCarrierHaveShipmentExtrasFields = false;
    this.selectedActiveProviders = [];
    this.loadedCarrier = null;
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes) {
      this.loadCarrierAutocomplete();
    }

    if (this.loadedCarrier && Object.keys(this.loadedCarrier).length > 0) {
      this.activeCarrier = { ...this.loadedCarrier };
      this.checkoutService.setSelectedCarrier(this.activeCarrier);

      this.carrierMetadata = this.formGroup.controls['metadata'].value;
      this.displayProvidersInfoOrServiceLevels = displayProvidersData(this.loadedCarrier);
      this.searchCarrier = this.loadedCarrier.name ?? '';

      if (changes.shipmentExtrasValues?.currentValue) {
        const shipmentExtrasValues = changes.shipmentExtrasValues.currentValue;
        this.initCarrierConfiguration(this.loadedCarrier, shipmentExtrasValues);
      } else if (isCarrierShippable(this.loadedCarrier)) {
        this.initCarrierConfiguration(this.loadedCarrier);
      }
    }
  }

  initCarrierConfiguration(carrier: Carrier, shipmentExtrasValues?: ShipmentExtras) {
    this.displayProvidersInfoOrServiceLevels = displayProvidersData(this.activeCarrier);
    this.onCarrierSelectInitShipmentExtras(shipmentExtrasValues);
    this.onCarrierChangeResetInternationalSettings();
    this.changeDetectorRef.detectChanges();
    this.formGroup.controls.carrierId.patchValue(this.activeCarrier);
  }

  onCarrierSelected(event) {
    if (!event || !Object.keys(event).length) {
      return;
    }

    this.activeCarrier = event?.source?.value ?? event;
    this.checkoutService.setSelectedCarrier(this.activeCarrier);
    this.initCarrierConfiguration(this.activeCarrier);
    this.changeDetectorRef.detectChanges();
    this.formGroup.controls.carrierId.patchValue(this.activeCarrier);
  }

  onCarrierChangeResetInternationalSettings() {
    this.formGroup.get('international').get('ddpConfig').reset();
  }

  onProvierServiceLevelSelected(value) {
    const metadata = this.carrierMetadata ?? {
      carrier_settings: {},
    };
    metadata.carrier_settings.providerService = value;
    this.carrierMetadata = metadata;
    this.formGroup.controls['metadata'].setValue(metadata);
  }

  onCarrierSelectInitShipmentExtras(shipmentExtrasValues?: ShipmentExtras) {
    this.isActiveServiceProviders = false;
    this.isCarrierAdvancedSettings = false;
    this.isActiveCarrierHaveShipmentExtrasFields = false;
    this.providerServices = [];

    if (this.activeCarrier?.providers?.length > 0 && this.displayProvidersInfoOrServiceLevels === 'providers') {
      this.activeCarrier.providers = this.activeCarrier.providers.filter((provider) => provider.enabled == true);
      this.isActiveServiceProviders = true;
    } else if (
      this.activeCarrier?.providers?.length > 0 &&
      this.displayProvidersInfoOrServiceLevels === 'providerServices'
    ) {
      this.activeCarrier?.providers.forEach((provider) => {
        if (!provider.enabled) {
          return;
        }
        provider.providerServices.forEach((service) => {
          if (!service.enabled) {
            return;
          }
          this.providerServices.push(`${provider['name']} - ${service['displayName']}`);
        });
      });

      if (this.providerServices?.length) {
        this.isActiveServiceProviders = true;
      }
    } else {
      // do nothing
    }

    // load shipment options
    this.formService.getCarrierTypeShipmentOptionsFields(this.activeCarrier?.id).subscribe(
      (shipmentExtras) => {
        if (shipmentExtras.fields?.length > 0) {
          this.isActiveCarrierHaveShipmentExtrasFields = true;
          this.isCarrierAdvancedSettings = true;

          this.shipmentExtrasFields = shipmentExtras.fields;
          this.initShipmentExtrasFields(shipmentExtrasValues);
        }
      },
      (error) => {
        console.log(error);
        this.shipmentExtrasFormGroup.addControl('invalidCarrierShipmentExtras', new UntypedFormControl(true));
      },
    );
  }

  onProviderSelect(value: any, provider: CarrierProviders) {
    if (!provider) {
      return;
    }

    if (value) {
      this.selectedActiveProviders.push(provider.name?.toLowerCase() as CarrierProvidersNames);
    }

    if (!value) {
      const index: number = this.selectedActiveProviders.findIndex((value) => value === provider.name?.toLowerCase());
      this.selectedActiveProviders.splice(index, 1);
    }

    this.setShipmentExtrasField('activeServiceProviders', this.selectedActiveProviders ?? undefined);
  }

  initShipmentExtrasFields(shipmentExtrasValues: ShipmentExtras) {
    if (!isNaN(shipmentExtrasValues?.id)) {
      this.formGroup.addControl('shipmentExtrasId', new UntypedFormControl(shipmentExtrasValues.id));
    }

    this.shipmentExtrasFields.forEach((field) => {
      if (shipmentExtrasValues) {
        this.shipmentExtrasFormGroup.addControl(
          field.fieldName,
          new UntypedFormControl(shipmentExtrasValues[field.fieldName]),
        );
      } else {
        this.shipmentExtrasFormGroup.addControl(field.fieldName, new UntypedFormControl(field.defaultValue));
      }
    });
    this.shipmentExtrasFormGroup.addControl('activeServiceProviders', new UntypedFormControl([]));

    if (shipmentExtrasValues?.activeServiceProviders) {
      this.selectedActiveProviders = shipmentExtrasValues.activeServiceProviders;
      this.setShipmentExtrasField('activeServiceProviders', this.selectedActiveProviders ?? undefined);
    }

    this.shipmentExtrasFormGroup.removeControl('invalidCarrierShipmentExtras');
    if (this.loadedCarrier) this.shipmentExtrasLoaded.emit();
  }

  getDisplayFn(value: CarrierListResponse): any {
    return value?.name ? value?.name : value;
  }

  findDefaultOption(options: Options[], control: any) {
    if (!control) {
      return;
    }

    return options.find((opt) => opt.value === control.value);
  }
  isInternationalMethod() {
    return internationalMethods.includes(this.shippingMethodCategory as ShippingMethod);
  }
  loadCarrierAutocomplete(): void {
    const autoCompleteParams = {
      supportsInternationalShipping: this.isInternationalMethod(),
    };
    this.formGroup
      .get('carrierId')
      .valueChanges.pipe(
        startWith(''),
        distinctUntilChanged(),
        debounceTime(300),
        tap(() => {
          this.isCarrierAutocompleteLoading = true;
        }),
        switchMap(() =>
          this.formService
            .carrierAutoComplete(this.searchCarrier?.name ?? this.searchCarrier, autoCompleteParams)
            .pipe(finalize(() => (this.isCarrierAutocompleteLoading = false))),
        ),
      )
      .subscribe((carriersListResponse) => {
        this.carrierAutocompleteResults = carriersListResponse.results ?? [];
      });
  }

  setShipmentExtrasField(control: string, value: any): any {
    return this.shipmentExtrasFormGroup['controls'][control]?.setValue(value);
  }

  getShipmentExtrasFieldValue(controlName: string): any {
    return (
      this?.shipmentExtrasValues?.[controlName] ||
      this?.shipmentExtrasFormGroup['controls']?.[controlName]?.value ||
      this?.shipmentExtrasFields.find((field) => field?.fieldName === controlName)?.defaultValue
    );
  }

  getProviderDisplayNameMap(provider: { name: string }) {
    const providerDisplayName = carrierProviderDisplayNameMap[provider.name]?.toLowerCase() || provider.name;
    return providerDisplayName;
  }
}
